

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useModal } from '@/contexts/ModalContext'
import { useEpisode } from '@/contexts/EpisodeContext'
import AssetsBrowserModal from '@/components/modals/AssetsBrowserModal'
import ResolutionModal from '@/components/modals/ResolutionModal'
import useFileUpload from '@/hooks/useFileUpload'
import { IoMdPause, IoMdPlay } from 'react-icons/io'
import { FiZoomIn, FiZoomOut, FiPlus, FiImage, FiMaximize, FiType, FiUpload, FiBold, FiAlignLeft, FiAlignCenter, FiAlignRight, FiMinus, FiMessageCircle, FiZap, FiFilm, FiVolume2, FiEye, FiEyeOff, FiEdit2, FiChevronsDown, FiChevronDown, FiChevronUp, FiChevronsUp, FiInfo, FiDownload } from 'react-icons/fi'
import { RiSendToBack, RiBringForward, RiSendBackward, RiBringToFront} from "react-icons/ri"
import { TbMagnet } from 'react-icons/tb'
import { LuLetterText } from 'react-icons/lu'
import * as Popover from '@radix-ui/react-popover'
import { RgbaColorPicker } from 'react-colorful'
import Button from '@/components/ui/Button'
import StoryboardComments from '@/components/storyboard/StoryboardComments'
import StoryboardGenerations, { GENERATION_DRAG_TYPE } from '@/components/storyboard/StoryboardGenerations'
import { DragPreviewProvider } from '@/components/ui/DragPreview'
import { SCRIPT_DRAG_TYPE } from '@/components/storyboard/StoryboardTimeline'
import DrawingCanvas from '@/components/DrawingCanvas'

// Canvas constants
const CANVAS_W = 10000
const CANVAS_H = 10000
const DEFAULT_CANVAS_ZOOM = 1

// Default element sizing when adding to canvas
const DEFAULT_ITEM_SCALE = 0.3            // fraction of original size for images/videos
const DEFAULT_ITEM_FALLBACK_W = 2         // fallback width % when natural size unavailable
const DEFAULT_ITEM_FALLBACK_H = 1         // fallback height %

// Default text element (screen pixels, converted to canvas % at current zoom when adding)
const DEFAULT_TEXT_BOX_SCREEN_W = 200
const DEFAULT_TEXT_BOX_SCREEN_H = 40
const DEFAULT_TEXT_FONT_SIZE = 16

// Default sketch element (1920×1080 internal resolution)
const SKETCH_CANVAS_W = 1920
const SKETCH_CANVAS_H = 1080
const SKETCH_WIDTH_PCT = (SKETCH_CANVAS_W / CANVAS_W) * 100   // 19.2%
const SKETCH_HEIGHT_PCT = (SKETCH_CANVAS_H / CANVAS_H) * 100  // 10.8%

// Audio element on canvas: fixed small size (not resizable)
const AUDIO_BOX_WIDTH_PCT = 1.5
const AUDIO_BOX_HEIGHT_PCT = 0.4

// --- Color helpers for rgba string ↔ {r,g,b,a} object ---
function parseColorToRgba(color) {
    if (!color || color === 'transparent') return { r: 0, g: 0, b: 0, a: 0 }
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
    if (rgbaMatch) {
        return { r: parseInt(rgbaMatch[1]), g: parseInt(rgbaMatch[2]), b: parseInt(rgbaMatch[3]), a: rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1 }
    }
    if (color.startsWith('#')) {
        const hex = color.length === 4 ? '#' + color[1]+color[1]+color[2]+color[2]+color[3]+color[3] : color
        return { r: parseInt(hex.slice(1, 3), 16), g: parseInt(hex.slice(3, 5), 16), b: parseInt(hex.slice(5, 7), 16), a: 1 }
    }
    return { r: 0, g: 0, b: 0, a: 1 }
}

function rgbaToString({ r, g, b, a }) {
    return `rgba(${r}, ${g}, ${b}, ${a})`
}

function rgbaToHex({ r, g, b }) {
    return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')
}

// --- Color picker swatch with popover ---
function ColorPickerSwatch({ label, value, onChange }) {
    const rgba = parseColorToRgba(value)
    return (
        <div className='flex items-center gap-1'>
            {label && <label className='text-[10px] text-secondary-foreground/50'>{label}</label>}
            <Popover.Root>
                <Popover.Trigger asChild>
                    <button
                        className='w-5 h-5 rounded-sm border border-secondary-foreground/20 cursor-pointer relative overflow-hidden'
                        style={{ background: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 8px 8px' }}
                        title={value || 'transparent'}
                    >
                        <span className='absolute inset-0 rounded-sm' style={{ background: value || 'transparent' }} />
                    </button>
                </Popover.Trigger>
                <Popover.Portal>
                    <Popover.Content sideOffset={6} className='z-50 rounded-md border bg-popover p-3 shadow-md flex flex-col gap-2.5' onClick={(e) => e.stopPropagation()}>
                        <RgbaColorPicker
                            color={rgba}
                            onChange={(c) => onChange(rgbaToString(c))}
                        />
                        <div className='flex items-center gap-1.5'>
                            <input
                                type='text'
                                className='flex-1 text-[10px] bg-secondary border rounded-sm px-1.5 py-1 text-secondary-foreground outline-none font-mono'
                                value={rgbaToHex(rgba)}
                                onChange={(e) => {
                                    const v = e.target.value
                                    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                                        const parsed = parseColorToRgba(v)
                                        onChange(rgbaToString({ ...parsed, a: rgba.a }))
                                    }
                                }}
                            />
                            <span className='text-[10px] text-secondary-foreground/50'>{Math.round(rgba.a * 100)}%</span>
                            {rgba.a > 0 && (
                                <button
                                    className='text-[10px] text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer'
                                    onClick={() => onChange('transparent')}
                                    title='Clear / transparent'
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </div>
    )
}

// --- Media helpers ---
function getItemMedia(item) {
    if (!item) return { url: null, thumbUrl: null, mediaType: null }
    switch (item.type) {
        case 'generation': {
            const gen = item.generation
            return { url: gen?.url, thumbUrl: gen?.thumbnail_url, mediaType: gen?.type }
        }
        case 'url':
            return { url: item.url, thumbUrl: null, mediaType: item.mediaType }
        case 'text':
        case 'script':
            return { url: null, thumbUrl: null, mediaType: 'text' }
        case 'sketch':
            return { url: item.url || null, thumbUrl: item.url || null, mediaType: 'image' }
        case 'asset':
        default: {
            const a = item.asset
            return { url: a?.url, thumbUrl: a?.thumbnail_url, mediaType: a?.mediaType }
        }
    }
}

// Get a display name from URL (e.g. for audio when no name is provided)
function getFilenameFromUrl(url) {
    if (!url || typeof url !== 'string') return ''
    try {
        const pathname = new URL(url).pathname
        const segment = pathname.split('/').filter(Boolean).pop()
        return segment ? decodeURIComponent(segment) : ''
    } catch {
        return ''
    }
}

function getNaturalSize(url, mediaType) {
    return new Promise((resolve) => {
        if (!url) return resolve(null)
        if (mediaType === 'image') {
            const img = new Image()
            img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
            img.onerror = () => resolve(null)
            img.src = url
        } else if (mediaType === 'video') {
            const vid = document.createElement('video')
            vid.onloadedmetadata = () => resolve({ w: vid.videoWidth, h: vid.videoHeight })
            vid.onerror = () => resolve(null)
            vid.src = url
        } else {
            resolve(null)
        }
    })
}

export default function StoryboardCanvas({
    storyboard,
    updateStoryboard,
    selectedElementIndex,
    setSelectedElementIndex,
    loading,
    deselectItemsTrigger,
    highlightCommentId,
    activeScriptId,
}) {
    const { openModal } = useModal()
    const { episode, updateScript } = useEpisode()
    const { uploadFile, isUploading } = useFileUpload()

    // Helper to find a script in the episode data by its _id
    const findScriptInEpisode = useCallback((scriptId) => {
        if (!episode || !scriptId) return null
        for (const seg of episode.segments || []) {
            for (const sc of seg.scenes || []) {
                const found = sc.script?.find(e => e._id === scriptId)
                if (found) return found
            }
        }
        return null
    }, [episode])

    // --- Refs ---
    const fileInputRef = useRef(null)
    const canvasRef = useRef(null)
    const canvasSnapLock = useRef({ x: null, y: null })
    const editingTextRef = useRef(null)
    const editingAudioNameRef = useRef(null)
    const sketchCanvasRef = useRef(null)
    const videoRefs = useRef({})
    const audioRefs = useRef({})
    const panStartRef = useRef(null)
    const canvasViewMapRef = useRef({})
    const prevElementIndexRef = useRef(null)
    const pointerOverCanvasRef = useRef(false)
    const selectedElementRef = useRef(null)

    // --- State ---
    const [selectedItemIndices, setSelectedItemIndices] = useState(new Set())
    const [canvasZoom, setCanvasZoom] = useState(DEFAULT_CANVAS_ZOOM)
    const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 })
    const [isPanning, setIsPanning] = useState(false)
    const [canvasDrag, setCanvasDrag] = useState(null)
    const [canvasResize, setCanvasResize] = useState(null)
    const [editingTextItem, setEditingTextItem] = useState(null)
    const [editingAudioNameItem, setEditingAudioNameItem] = useState(null)
    const [editingSketchItem, setEditingSketchItem] = useState(null)
    const [sketchMode, setSketchMode] = useState('draw')
    const [uploadingSketchItem, setUploadingSketchItem] = useState(null)
    const [sketchPreview, setSketchPreview] = useState(null) // { index, dataUrl } — local preview while uploading
    const [playingVideos, setPlayingVideos] = useState({})
    const [playingAudio, setPlayingAudio] = useState({})
    const [canvasSnapEnabled, setCanvasSnapEnabled] = useState(true)
    const [textElementsVisible, setTextElementsVisible] = useState(true)
    const [visualElementsVisible, setVisualElementsVisible] = useState(true)
    const [commentsOpen, setCommentsOpen] = useState(!!highlightCommentId)
    const [generationsOpen, setGenerationsOpen] = useState(false)
    const [showTypes, setShowTypes] = useState(false)
    const [dropTargetItemIndex, setDropTargetItemIndex] = useState(null)

    // --- Derived ---
    const selectedElement = storyboard?.elements?.[selectedElementIndex] || null
    selectedElementRef.current = selectedElement

    const selectedTextItem = useMemo(() => {
        if (selectedElementIndex === null || selectedItemIndices.size !== 1) return null
        const idx = [...selectedItemIndices][0]
        const item = selectedElement?.items?.[idx]
        return (item?.type === 'text' || item?.type === 'script') ? { ...item, _idx: idx } : null
    }, [selectedElementIndex, selectedItemIndices, selectedElement])

    const selectedSketchItem = useMemo(() => {
        if (selectedElementIndex === null || selectedItemIndices.size !== 1) return null
        const idx = [...selectedItemIndices][0]
        const item = selectedElement?.items?.[idx]
        return item?.type === 'sketch' ? { ...item, _idx: idx } : null
    }, [selectedElementIndex, selectedItemIndices, selectedElement])

    // Compute preview from selected canvas item for AI generations
    const selectedItemPreview = useMemo(() => {
        if (selectedElementIndex === null || selectedItemIndices.size !== 1) return null
        const idx = [...selectedItemIndices][0]
        const item = selectedElement?.items?.[idx]
        if (!item || item.type === 'text' || item.type === 'script') return null
        const { url, mediaType } = getItemMedia(item)
        if (!url) return null
        return { url, type: mediaType }
    }, [selectedElementIndex, selectedItemIndices, selectedElement])

    // Determine which selected items are downloadable (image, video, audio)
    const downloadableSelectedItems = useMemo(() => {
        if (selectedElementIndex === null || selectedItemIndices.size === 0) return []
        const items = selectedElement?.items || []
        const result = []
        for (const idx of selectedItemIndices) {
            const item = items[idx]
            if (!item) continue
            const { url, mediaType } = getItemMedia(item)
            if (!url || (mediaType !== 'image' && mediaType !== 'video' && mediaType !== 'audio')) continue
            // Derive file name: prefer underlying asset name, then item name, then filename from URL
            const assetName = item.asset?.name || item.asset?.originalFilename || item.generation?.name || item.name
            const fileName = assetName || getFilenameFromUrl(url)
            result.push({ url, fileName })
        }
        return result
    }, [selectedElementIndex, selectedItemIndices, selectedElement])

    const downloadSelectedItems = useCallback(async () => {
        if (downloadableSelectedItems.length === 0) return
        for (const { url, fileName } of downloadableSelectedItems) {
            try {
                const response = await fetch(url)
                const blob = await response.blob()
                const blobUrl = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = blobUrl
                a.download = fileName || 'download'
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(blobUrl)
            } catch (err) {
                console.error('Download failed:', err)
            }
        }
    }, [downloadableSelectedItems])

    // --- Deselect items when parent requests ---
    useEffect(() => {
        setSelectedItemIndices(new Set())
    }, [deselectItemsTrigger])

    // --- Pause videos and exit text/sketch editing on element switch ---
    useEffect(() => {
        Object.values(videoRefs.current).forEach(v => { try { v.pause() } catch {} })
        setPlayingVideos({})
        setEditingTextItem(null)
        setEditingSketchItem(null)
    }, [selectedElementIndex])

    // ==============================
    // CANVAS ITEM CRUD
    // ==============================

    const computeNewItemPlacement = async (url, mediaType) => {
        let widthPct, heightPct
        if (mediaType === 'audio') {
            widthPct = AUDIO_BOX_WIDTH_PCT
            heightPct = AUDIO_BOX_HEIGHT_PCT
        } else {
            const size = await getNaturalSize(url, mediaType)
            if (size) {
                widthPct = ((size.w / CANVAS_W) * 100) * DEFAULT_ITEM_SCALE
                heightPct = ((size.h / CANVAS_H) * 100) * DEFAULT_ITEM_SCALE
            } else {
                widthPct = DEFAULT_ITEM_FALLBACK_W
                heightPct = DEFAULT_ITEM_FALLBACK_H
            }
        }
        let centerXPct = 50 - widthPct / 2
        let centerYPct = 50 - heightPct / 2
        const canvasEl = canvasRef.current
        if (canvasEl) {
            const rect = canvasEl.getBoundingClientRect()
            const viewCenterX = (rect.width / 2 - canvasPan.x) / canvasZoom
            const viewCenterY = (rect.height / 2 - canvasPan.y) / canvasZoom
            centerXPct = Math.max(0, Math.min(100 - widthPct, (viewCenterX / CANVAS_W) * 100 - widthPct / 2))
            centerYPct = Math.max(0, Math.min(100 - heightPct, (viewCenterY / CANVAS_H) * 100 - heightPct / 2))
        }
        return { x: centerXPct, y: centerYPct, width: widthPct, height: heightPct }
    }

    const pushCanvasItem = (itemData) => {
        let newIndex
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            newIndex = el.items.length
            el.items = [...el.items, { ...itemData, zIndex: newIndex }]
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
        setSelectedItemIndices(new Set([newIndex]))
    }

    const addCanvasItem = async () => {
        if (selectedElementIndex === null) return
        const asset = await openModal(<AssetsBrowserModal header='Add Asset to Canvas' />)
        if (!asset) return
        const placement = await computeNewItemPlacement(asset.url || asset.thumbnail_url, asset.mediaType)
        const name = asset.mediaType === 'audio' ? (asset.name || asset.originalFilename || getFilenameFromUrl(asset.url || asset.thumbnail_url) || 'Audio') : undefined
        pushCanvasItem({ type: 'asset', asset, ...(name != null ? { name } : {}), ...placement })
    }

    const addCanvasFromUrl = async (url, mediaType, name) => {
        if (selectedElementIndex === null || !url) return
        const placement = await computeNewItemPlacement(url, mediaType)
        const displayName = mediaType === 'audio' ? (name || getFilenameFromUrl(url) || 'Audio') : undefined
        pushCanvasItem({ type: 'url', url, mediaType, ...(displayName != null ? { name: displayName } : {}), ...placement })
    }

    // Convert screen coordinates to canvas percentage (for drop)
    const clientToCanvasPct = useCallback((clientX, clientY) => {
        const el = canvasRef.current
        if (!el) return null
        const rect = el.getBoundingClientRect()
        const canvasPxX = (clientX - rect.left - canvasPan.x) / canvasZoom
        const canvasPxY = (clientY - rect.top - canvasPan.y) / canvasZoom
        const xPct = (canvasPxX / CANVAS_W) * 100
        const yPct = (canvasPxY / CANVAS_H) * 100
        return { xPct, yPct }
    }, [canvasPan, canvasZoom])

    // Topmost image or video item index at (xPct, yPct), or null
    const getReplaceableItemAt = useCallback((xPct, yPct) => {
        const items = selectedElement?.items || []
        for (let i = items.length - 1; i >= 0; i--) {
            const item = items[i]
            const { mediaType } = getItemMedia(item)
            if (mediaType !== 'image' && mediaType !== 'video') continue
            const { x, y, width, height } = item
            if (xPct >= x && xPct <= x + width && yPct >= y && yPct <= y + height) return i
        }
        return null
    }, [selectedElement])

    // Placement with center at drop point (for adding new item)
    const computePlacementAtDrop = useCallback(async (url, mediaType, centerXPct, centerYPct) => {
        let widthPct, heightPct
        if (mediaType === 'audio') {
            widthPct = AUDIO_BOX_WIDTH_PCT
            heightPct = AUDIO_BOX_HEIGHT_PCT
        } else {
            const size = await getNaturalSize(url, mediaType)
            if (size) {
                widthPct = ((size.w / CANVAS_W) * 100) * DEFAULT_ITEM_SCALE
                heightPct = ((size.h / CANVAS_H) * 100) * DEFAULT_ITEM_SCALE
            } else {
                widthPct = DEFAULT_ITEM_FALLBACK_W
                heightPct = DEFAULT_ITEM_FALLBACK_H
            }
        }
        const x = Math.max(0, Math.min(100 - widthPct, centerXPct - widthPct / 2))
        const y = Math.max(0, Math.min(100 - heightPct, centerYPct - heightPct / 2))
        return { x, y, width: widthPct, height: heightPct }
    }, [])

    const replaceCanvasItemUrl = useCallback((itemIndex, url, mediaType) => {
        if (selectedElementIndex === null) return
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            const items = [...el.items]
            items[itemIndex] = { ...items[itemIndex], type: 'url', url, mediaType }
            el.items = items
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
    }, [selectedElementIndex, updateStoryboard])

    const addScriptToCanvasAtPosition = useCallback((scriptId, centerXPct, centerYPct) => {
        if (selectedElementIndex === null || !scriptId) return
        const widthPct = Math.max(1, Math.min(30, (DEFAULT_TEXT_BOX_SCREEN_W / canvasZoom / CANVAS_W) * 100))
        const heightPct = Math.max(0.5, Math.min(10, (DEFAULT_TEXT_BOX_SCREEN_H / canvasZoom / CANVAS_H) * 100))
        const x = Math.max(0, Math.min(100 - widthPct, centerXPct - widthPct / 2))
        const y = Math.max(0, Math.min(100 - heightPct, centerYPct - heightPct / 2))
        pushCanvasItem({
            type: 'script',
            script: scriptId,
            fontSize: 12,
            color: '#000000',
            background: '#ffffff',
            x,
            y,
            width: widthPct,
            height: heightPct,
        })
    }, [selectedElementIndex, canvasZoom, pushCanvasItem])

    const handleCanvasDrop = useCallback(async (e) => {
        if (selectedElementIndex === null) return
        e.preventDefault()
        setDropTargetItemIndex(null)

        // Script drag: add script item at drop position
        if (e.dataTransfer.types.includes(SCRIPT_DRAG_TYPE)) {
            const raw = e.dataTransfer.getData(SCRIPT_DRAG_TYPE)
            if (!raw) return
            let payload
            try {
                payload = JSON.parse(raw)
            } catch {
                return
            }
            const { _id: scriptId } = payload
            if (!scriptId) return
            const coords = clientToCanvasPct(e.clientX, e.clientY)
            if (!coords) return
            addScriptToCanvasAtPosition(scriptId, coords.xPct, coords.yPct)
            return
        }

        // Generation drag: add or replace media
        const raw = e.dataTransfer.getData(GENERATION_DRAG_TYPE)
        if (!raw) return
        let payload
        try {
            payload = JSON.parse(raw)
        } catch {
            return
        }
        const { url, type: mediaType } = payload
        if (!url || !mediaType) return
        const coords = clientToCanvasPct(e.clientX, e.clientY)
        if (!coords) return
        const { xPct, yPct } = coords
        const replaceIndex = getReplaceableItemAt(xPct, yPct)
        if (replaceIndex !== null && (mediaType === 'image' || mediaType === 'video')) {
            replaceCanvasItemUrl(replaceIndex, url, mediaType)
        } else {
            const placement = await computePlacementAtDrop(url, mediaType, xPct, yPct)
            const name = mediaType === 'audio' ? (getFilenameFromUrl(url) || 'Audio') : undefined
            pushCanvasItem({ type: 'url', url, mediaType, ...(name != null ? { name } : {}), ...placement })
        }
    }, [selectedElementIndex, clientToCanvasPct, getReplaceableItemAt, replaceCanvasItemUrl, computePlacementAtDrop, addScriptToCanvasAtPosition])

    const handleCanvasDragOver = useCallback((e) => {
        if (e.dataTransfer.types.includes(SCRIPT_DRAG_TYPE)) {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
            return
        }
        if (e.dataTransfer.types.includes(GENERATION_DRAG_TYPE)) {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
            const coords = clientToCanvasPct(e.clientX, e.clientY)
            const index = coords ? getReplaceableItemAt(coords.xPct, coords.yPct) : null
            setDropTargetItemIndex(prev => (prev === index ? prev : index))
        } else {
            setDropTargetItemIndex(null)
        }
    }, [clientToCanvasPct, getReplaceableItemAt])

    const handleGenerationDragLeave = useCallback((e) => {
        // Only clear when actually leaving the canvas (not when entering a child)
        const el = canvasRef.current
        if (!el || !e.relatedTarget) return
        if (!el.contains(e.relatedTarget)) setDropTargetItemIndex(null)
    }, [])

    const addCanvasUpload = () => {
        if (selectedElementIndex === null) return
        fileInputRef.current?.click()
    }

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        e.target.value = ''
        try {
            const result = await uploadFile(file, 'storyboard')
            if (!result?.url) return
            const placement = await computeNewItemPlacement(result.url, result.mediaType)
            pushCanvasItem({ type: 'url', url: result.url, mediaType: result.mediaType, name: file.name, ...placement })
        } catch (err) {
            console.error('Upload failed:', err)
        }
    }

    const addCanvasText = () => {
        if (selectedElementIndex === null) return
        const canvasEl = canvasRef.current
        const widthPct = Math.max(1, Math.min(30, (DEFAULT_TEXT_BOX_SCREEN_W / canvasZoom / CANVAS_W) * 100))
        const heightPct = Math.max(0.5, Math.min(10, (DEFAULT_TEXT_BOX_SCREEN_H / canvasZoom / CANVAS_H) * 100))
        const fontSize = DEFAULT_TEXT_FONT_SIZE
        let centerXPct = 50 - widthPct / 2
        let centerYPct = 50 - heightPct / 2
        if (canvasEl) {
            const rect = canvasEl.getBoundingClientRect()
            const viewCenterX = (rect.width / 2 - canvasPan.x) / canvasZoom
            const viewCenterY = (rect.height / 2 - canvasPan.y) / canvasZoom
            centerXPct = Math.max(0, Math.min(100 - widthPct, (viewCenterX / CANVAS_W) * 100 - widthPct / 2))
            centerYPct = Math.max(0, Math.min(100 - heightPct, (viewCenterY / CANVAS_H) * 100 - heightPct / 2))
        }
        pushCanvasItem({ type: 'text', text: 'Text', fontSize, color: '#000000', background: '#ffffff', x: centerXPct, y: centerYPct, width: widthPct, height: heightPct })
    }

    const addCanvasSketch = async () => {
        if (selectedElementIndex === null) return
        const result = await openModal(<ResolutionModal width={SKETCH_CANVAS_W} height={SKETCH_CANVAS_H} title='Sketch Resolution' />)
        if (!result) return
        const { width: sketchW, height: sketchH } = result
        const widthPct = (sketchW / CANVAS_W) * 100 * DEFAULT_ITEM_SCALE
        const heightPct = (sketchH / CANVAS_H) * 100 * DEFAULT_ITEM_SCALE
        const canvasEl = canvasRef.current
        let centerXPct = 50 - widthPct / 2
        let centerYPct = 50 - heightPct / 2
        if (canvasEl) {
            const rect = canvasEl.getBoundingClientRect()
            const viewCenterX = (rect.width / 2 - canvasPan.x) / canvasZoom
            const viewCenterY = (rect.height / 2 - canvasPan.y) / canvasZoom
            centerXPct = Math.max(0, Math.min(100 - widthPct, (viewCenterX / CANVAS_W) * 100 - widthPct / 2))
            centerYPct = Math.max(0, Math.min(100 - heightPct, (viewCenterY / CANVAS_H) * 100 - heightPct / 2))
        }
        pushCanvasItem({
            type: 'sketch',
            brushColor: '#000000',
            bgColor: '#ffffff',
            brushSize: 3,
            canvasWidth: sketchW,
            canvasHeight: sketchH,
            x: centerXPct,
            y: centerYPct,
            width: widthPct,
            height: heightPct,
        })
    }

    const startEditingSketch = (itemIndex) => {
        setSketchMode('draw')
        setEditingSketchItem(itemIndex)
    }

    const commitSketchEdit = async () => {
        if (editingSketchItem === null || selectedElementIndex === null) return
        const itemIndex = editingSketchItem

        const sketchRef = sketchCanvasRef.current
        if (!sketchRef) return

        const base64 = sketchRef.getImageBase64()
        if (!base64) return

        const wbgBase64 = sketchRef.getDisplayBase64Jpeg()

        // Capture a composite preview (bg + strokes) before unmounting the DrawingCanvas
        const previewUrl = sketchRef.getDisplayBase64() || base64
        setSketchPreview({ index: itemIndex, dataUrl: previewUrl })

        setEditingSketchItem(null)
        setUploadingSketchItem(itemIndex)

        const timestamp = Date.now()

        // Convert base64 data URL to a File for upload
        const res = await fetch(base64)
        const blob = await res.blob()
        const file = new File([blob], `sketch-${timestamp}.png`, { type: 'image/png' })

        try {
            const result = await uploadFile(file, 'temp')

            // Also upload a version with the background color as JPEG
            let wbgUrl = null
            if (wbgBase64) {
                try {
                    const wbgRes = await fetch(wbgBase64)
                    const wbgBlob = await wbgRes.blob()
                    const wbgFile = new File([wbgBlob], `sketch-${timestamp}_wbg.jpg`, { type: 'image/jpeg' })
                    const wbgResult = await uploadFile(wbgFile, 'temp')
                    wbgUrl = wbgResult?.url || null
                } catch (err) {
                    console.error('Failed to upload sketch with bg:', err)
                }
            }

            if (result?.url) {
                updateStoryboard(prev => {
                    const elements = [...prev.elements]
                    const el = { ...elements[selectedElementIndex] }
                    el.items = [...el.items]
                    el.items[itemIndex] = { ...el.items[itemIndex], url: result.url, sketchCompositeUrl: wbgUrl }
                    elements[selectedElementIndex] = el
                    return { ...prev, elements }
                })
            }
        } catch (err) {
            console.error('Failed to upload sketch:', err)
        } finally {
            setUploadingSketchItem(null)
            setSketchPreview(null)
        }
    }

    const updateSketchItemProps = (itemIndex, props) => {
        if (selectedElementIndex === null) return
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            el.items = [...el.items]
            el.items[itemIndex] = { ...el.items[itemIndex], ...props }
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
    }

    const startEditingText = (itemIndex) => {
        setEditingTextItem(itemIndex)
        setTimeout(() => editingTextRef.current?.focus(), 0)
    }

    const commitTextEdit = (itemIndex, newText) => {
        setEditingTextItem(null)
        if (selectedElementIndex === null) return

        const item = selectedElement?.items?.[itemIndex]

        // For script items, update the underlying Script model — not the storyboard's text field.
        // The speech is owned by the Script document; the storyboard just holds a reference.
        if (item?.type === 'script' && item.script) {
            const scriptId = typeof item.script === 'object' ? item.script._id : item.script
            // Prefer live episode context data; fall back to populated script from storyboard
            const scriptData = findScriptInEpisode(scriptId) || (typeof item.script === 'object' ? item.script : null)
            if (scriptData) {
                updateScript(scriptData, { speech: newText || '' })
            }
            return
        }

        // For plain text items, update the storyboard's text field as before
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            el.items = [...el.items]
            el.items[itemIndex] = { ...el.items[itemIndex], text: newText || 'Text' }
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
    }

    const updateTextItemProps = (itemIndex, props) => {
        if (selectedElementIndex === null) return
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            el.items = [...el.items]
            el.items[itemIndex] = { ...el.items[itemIndex], ...props }
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
    }

    const startEditingAudioName = (itemIndex) => {
        setEditingAudioNameItem(itemIndex)
        setTimeout(() => editingAudioNameRef.current?.focus(), 0)
    }

    const commitAudioNameEdit = (itemIndex, newName) => {
        setEditingAudioNameItem(null)
        if (selectedElementIndex === null) return
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            el.items = [...el.items]
            el.items[itemIndex] = { ...el.items[itemIndex], name: newName != null && newName.trim() !== '' ? newName.trim() : 'Audio' }
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
    }

    const deleteCanvasItems = useCallback(() => {
        if (selectedElementIndex === null || selectedItemIndices.size === 0) return
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            el.items = el.items.filter((_, i) => !selectedItemIndices.has(i))
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
        setSelectedItemIndices(new Set())
    }, [selectedElementIndex, selectedItemIndices, updateStoryboard])

    // Reorder selected canvas items (z-order). Lower index = further back.
    const sendToBack = useCallback(() => {
        if (selectedElementIndex === null || selectedItemIndices.size === 0) return
        const sorted = [...selectedItemIndices].sort((a, b) => a - b)
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            const items = el.items
            const selectedItems = sorted.map((i) => items[i])
            const unselected = items.filter((_, i) => !selectedItemIndices.has(i))
            const newOrder = [...selectedItems, ...unselected]
            el.items = newOrder.map((item, i) => ({ ...item, zIndex: i }))
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
        setSelectedItemIndices(new Set(Array.from({ length: sorted.length }, (_, i) => i)))
    }, [selectedElementIndex, selectedItemIndices, updateStoryboard])

    const moveBackward = useCallback(() => {
        if (selectedElementIndex === null || selectedItemIndices.size === 0) return
        const sorted = [...selectedItemIndices].sort((a, b) => a - b)
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            const newItems = [...el.items]
            for (const i of sorted) {
                if (i > 0) {
                    ;[newItems[i - 1], newItems[i]] = [newItems[i], newItems[i - 1]]
                }
            }
            el.items = newItems.map((item, i) => ({ ...item, zIndex: i }))
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
        setSelectedItemIndices(new Set(sorted.map((i) => (i > 0 ? i - 1 : i))))
    }, [selectedElementIndex, selectedItemIndices, updateStoryboard])

    const moveForward = useCallback(() => {
        if (selectedElementIndex === null || selectedItemIndices.size === 0) return
        const sorted = [...selectedItemIndices].sort((a, b) => b - a)
        const n = selectedElement?.items?.length ?? 0
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            const newItems = [...el.items]
            for (const i of sorted) {
                if (i < n - 1) {
                    ;[newItems[i], newItems[i + 1]] = [newItems[i + 1], newItems[i]]
                }
            }
            el.items = newItems.map((item, i) => ({ ...item, zIndex: i }))
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
        setSelectedItemIndices(new Set([...selectedItemIndices].map((i) => (i < n - 1 ? i + 1 : i))))
    }, [selectedElementIndex, selectedItemIndices, selectedElement?.items?.length, updateStoryboard])

    const bringToFront = useCallback(() => {
        if (selectedElementIndex === null || selectedItemIndices.size === 0) return
        const sorted = [...selectedItemIndices].sort((a, b) => a - b)
        updateStoryboard(prev => {
            const elements = [...prev.elements]
            const el = { ...elements[selectedElementIndex] }
            const items = el.items
            const selectedItems = sorted.map((i) => items[i])
            const unselected = items.filter((_, i) => !selectedItemIndices.has(i))
            const newOrder = [...unselected, ...selectedItems]
            el.items = newOrder.map((item, i) => ({ ...item, zIndex: i }))
            elements[selectedElementIndex] = el
            return { ...prev, elements }
        })
        const numUnselected = (selectedElement?.items?.length ?? 0) - sorted.length
        setSelectedItemIndices(new Set(sorted.map((_, i) => numUnselected + i)))
    }, [selectedElementIndex, selectedItemIndices, selectedElement?.items?.length, updateStoryboard])

    // ==============================
    // CANVAS DRAG & RESIZE
    // ==============================
    const handleCanvasPointerDown = (e, itemIndex) => {
        if (editingTextItem !== null && editingTextItem !== itemIndex) {
            editingTextRef.current?.blur()
        }
        if (editingAudioNameItem !== null && editingAudioNameItem !== itemIndex) {
            editingAudioNameRef.current?.blur()
        }
        if (editingSketchItem !== null && editingSketchItem !== itemIndex) {
            commitSketchEdit()
        }
        if (editingTextItem === itemIndex) return
        if (editingAudioNameItem === itemIndex) return
        if (editingSketchItem === itemIndex) return
        e.stopPropagation()
        e.preventDefault()
        const isMultiKey = e.ctrlKey || e.metaKey

        let newSelection
        if (isMultiKey) {
            newSelection = new Set(selectedItemIndices)
            if (newSelection.has(itemIndex)) {
                newSelection.delete(itemIndex)
            } else {
                newSelection.add(itemIndex)
            }
        } else if (!selectedItemIndices.has(itemIndex)) {
            newSelection = new Set([itemIndex])
        } else {
            newSelection = selectedItemIndices
        }
        setSelectedItemIndices(newSelection)

        if (newSelection.size > 0 && newSelection.has(itemIndex)) {
            const items = selectedElement.items
            const startPositions = {}
            for (const idx of newSelection) {
                startPositions[idx] = { x: items[idx].x, y: items[idx].y }
            }
            setCanvasDrag({
                index: itemIndex,
                indices: newSelection,
                startMouseX: e.clientX,
                startMouseY: e.clientY,
                startPositions,
            })
        }
    }

    const handleCanvasResizePointerDown = (e, itemIndex) => {
        e.stopPropagation()
        e.preventDefault()
        setSelectedItemIndices(new Set([itemIndex]))
        const item = selectedElement.items[itemIndex]
        const isFreeResize = item.type === 'text' || item.type === 'script'
        setCanvasResize({
            index: itemIndex,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startWidth: item.width,
            startHeight: item.height,
            aspectRatio: item.width / (item.height || 1),
            freeResize: isFreeResize,
        })
    }

    // Canvas snap helpers
    const getCanvasItemEdges = useCallback((excludeIndex) => {
        const items = selectedElement?.items || []
        const xEdges = []
        const yEdges = []
        items.forEach((item, i) => {
            if (i === excludeIndex) return
            xEdges.push(item.x, item.x + item.width)
            yEdges.push(item.y, item.y + item.height)
        })
        const thresholdPct = (8 / (CANVAS_W * canvasZoom)) * 100
        return { xEdges, yEdges, threshold: thresholdPct }
    }, [selectedElement, canvasZoom])

    const snapCanvasWithLock = useCallback((rawValue, itemSize, edges, threshold, axis) => {
        if (!canvasSnapEnabled) {
            canvasSnapLock.current[axis] = null
            return rawValue
        }
        const lock = canvasSnapLock.current[axis]
        const releaseThreshold = threshold * 1.5

        if (lock !== null) {
            const snappedEdge = lock.side === 'left' ? rawValue : rawValue + itemSize
            const distFromLock = Math.abs(snappedEdge - lock.edge)
            if (distFromLock < releaseThreshold) {
                return lock.side === 'left' ? lock.edge : lock.edge - itemSize
            }
            canvasSnapLock.current[axis] = null
        }

        let bestSnap = null
        let bestDist = threshold
        for (const edge of edges) {
            const d = Math.abs(rawValue - edge)
            if (d < bestDist) { bestDist = d; bestSnap = { edge, side: 'left', result: edge } }
        }
        for (const edge of edges) {
            const d = Math.abs((rawValue + itemSize) - edge)
            if (d < bestDist) { bestDist = d; bestSnap = { edge, side: 'right', result: edge - itemSize } }
        }

        if (bestSnap) {
            canvasSnapLock.current[axis] = { edge: bestSnap.edge, side: bestSnap.side }
            return bestSnap.result
        }
        return rawValue
    }, [canvasSnapEnabled])

    useEffect(() => {
        if (!canvasDrag && !canvasResize) return
        canvasSnapLock.current = { x: null, y: null }

        const handleMove = (e) => {
            const curElement = selectedElementRef.current
            if (canvasDrag) {
                const dx = ((e.clientX - canvasDrag.startMouseX) / (CANVAS_W * canvasZoom)) * 100
                const dy = ((e.clientY - canvasDrag.startMouseY) / (CANVAS_H * canvasZoom)) * 100

                const anchorStart = canvasDrag.startPositions[canvasDrag.index]
                const rawX = anchorStart.x + dx
                const rawY = anchorStart.y + dy
                const item = curElement?.items?.[canvasDrag.index]
                const w = item?.width || 0
                const h = item?.height || 0
                const dragIndices = canvasDrag.indices || new Set([canvasDrag.index])
                const allItems = curElement?.items || []
                const xEdges = [], yEdges = []
                allItems.forEach((it, i) => {
                    if (dragIndices.has(i)) return
                    xEdges.push(it.x, it.x + it.width)
                    yEdges.push(it.y, it.y + it.height)
                })
                const threshold = (8 / (CANVAS_W * canvasZoom)) * 100

                let newX = snapCanvasWithLock(rawX, w, xEdges, threshold, 'x')
                newX = Math.max(0, Math.min(100 - w, newX))
                let newY = snapCanvasWithLock(rawY, h, yEdges, threshold, 'y')
                newY = Math.max(0, Math.min(100 - h, newY))

                const snapDx = newX - anchorStart.x
                const snapDy = newY - anchorStart.y

                updateStoryboard(prev => {
                    const elements = [...prev.elements]
                    const el = { ...elements[selectedElementIndex] }
                    const items = [...el.items]
                    for (const idx of dragIndices) {
                        const sp = canvasDrag.startPositions[idx]
                        if (!sp) continue
                        items[idx] = {
                            ...items[idx],
                            x: Math.max(0, Math.min(100, sp.x + snapDx)),
                            y: Math.max(0, Math.min(100, sp.y + snapDy)),
                        }
                    }
                    el.items = items
                    elements[selectedElementIndex] = el
                    return { ...prev, elements }
                })
            }
            if (canvasResize) {
                const dxPct = ((e.clientX - canvasResize.startMouseX) / (CANVAS_W * canvasZoom)) * 100
                const dyPct = ((e.clientY - canvasResize.startMouseY) / (CANVAS_H * canvasZoom)) * 100

                const curItems = curElement?.items || []
                const thresholdPct = (8 / (CANVAS_W * canvasZoom)) * 100
                const xEdges = [], yEdges = []
                curItems.forEach((it, i) => {
                    if (i === canvasResize.index) return
                    xEdges.push(it.x, it.x + it.width)
                    yEdges.push(it.y, it.y + it.height)
                })

                const item = curElement?.items?.[canvasResize.index]
                const ix = item?.x || 0
                const iy = item?.y || 0
                const ar = canvasResize.aspectRatio

                if (canvasResize.freeResize) {
                    const rawWidth = canvasResize.startWidth + dxPct
                    const rawHeight = canvasResize.startHeight + dyPct
                    const rawRight = ix + rawWidth
                    const rawBottom = iy + rawHeight

                    const snappedRight = snapCanvasWithLock(rawRight, 0, xEdges, thresholdPct, 'x')
                    const snappedBottom = snapCanvasWithLock(rawBottom, 0, yEdges, thresholdPct, 'y')

                    const newWidth = Math.max(0.5, Math.min(100, snappedRight - ix))
                    const newHeight = Math.max(0.3, Math.min(100, snappedBottom - iy))

                    updateStoryboard(prev => {
                        const elements = [...prev.elements]
                        const el = { ...elements[selectedElementIndex] }
                        const items = [...el.items]
                        items[canvasResize.index] = { ...items[canvasResize.index], width: newWidth, height: newHeight }
                        el.items = items
                        elements[selectedElementIndex] = el
                        return { ...prev, elements }
                    })
                } else {
                    const rawWidth = canvasResize.startWidth + dxPct
                    const rawRight = ix + rawWidth
                    const rawHeight = rawWidth / ar
                    const rawBottom = iy + rawHeight

                    const snappedRight = snapCanvasWithLock(rawRight, 0, xEdges, thresholdPct, 'x')
                    const xDidSnap = snappedRight !== rawRight
                    const widthFromX = snappedRight - ix

                    const snappedBottom = snapCanvasWithLock(rawBottom, 0, yEdges, thresholdPct, 'y')
                    const yDidSnap = snappedBottom !== rawBottom
                    const heightFromY = snappedBottom - iy
                    const widthFromY = heightFromY * ar

                    let newWidth
                    if (xDidSnap && yDidSnap) {
                        newWidth = Math.abs(widthFromX - rawWidth) <= Math.abs(widthFromY - rawWidth) ? widthFromX : widthFromY
                    } else if (xDidSnap) {
                        newWidth = widthFromX
                    } else if (yDidSnap) {
                        newWidth = widthFromY
                    } else {
                        newWidth = rawWidth
                    }
                    newWidth = Math.max(0.5, Math.min(100, newWidth))
                    let newHeight = Math.max(0.5, Math.min(100, newWidth / ar))

                    updateStoryboard(prev => {
                        const elements = [...prev.elements]
                        const el = { ...elements[selectedElementIndex] }
                        const items = [...el.items]
                        items[canvasResize.index] = { ...items[canvasResize.index], width: newWidth, height: newHeight }
                        el.items = items
                        elements[selectedElementIndex] = el
                        return { ...prev, elements }
                    })
                }
            }
        }

        const handleUp = () => {
            setCanvasDrag(null)
            setCanvasResize(null)
        }

        window.addEventListener('pointermove', handleMove)
        window.addEventListener('pointerup', handleUp)
        return () => {
            window.removeEventListener('pointermove', handleMove)
            window.removeEventListener('pointerup', handleUp)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvasDrag, canvasResize, selectedElementIndex, updateStoryboard, canvasZoom, snapCanvasWithLock])

    // Deselect canvas item when clicking canvas background
    const handleCanvasBackgroundClick = () => {
        if (!isPanning) {
            setSelectedItemIndices(new Set())
            if (editingTextItem !== null) editingTextRef.current?.blur()
            if (editingAudioNameItem !== null) editingAudioNameRef.current?.blur()
            if (editingSketchItem !== null) commitSketchEdit()
        }
    }

    // ==============================
    // CANVAS VIEW SAVE / RESTORE
    // ==============================
    useEffect(() => {
        if (loading || selectedElementIndex === null) return
        const el = canvasRef.current
        if (!el) return

        const prev = prevElementIndexRef.current
        if (prev !== null && prev !== selectedElementIndex) {
            canvasViewMapRef.current[prev] = { zoom: canvasZoom, pan: { ...canvasPan } }
        }

        const saved = canvasViewMapRef.current[selectedElementIndex]
        if (saved) {
            setCanvasZoom(saved.zoom)
            setCanvasPan(saved.pan)
        } else {
            const element = storyboard?.elements?.[selectedElementIndex]
            const view = computeFrameView(element)
            if (view) {
                setCanvasZoom(view.zoom)
                setCanvasPan(view.pan)
            }
        }

        prevElementIndexRef.current = selectedElementIndex
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedElementIndex, loading])

    // ==============================
    // CANVAS ZOOM & PAN (Figma-style)
    // ==============================

    // Canvas zoom via wheel/pinch
    useEffect(() => {
        const el = canvasRef.current
        if (!el) return

        const onEnter = () => { pointerOverCanvasRef.current = true }
        const onLeave = () => { pointerOverCanvasRef.current = false }
        el.addEventListener('pointerenter', onEnter)
        el.addEventListener('pointerleave', onLeave)

        const onWheel = (e) => {
            if (!pointerOverCanvasRef.current) return
            e.preventDefault()
            e.stopImmediatePropagation()

            if (e.ctrlKey || e.metaKey) {
                // Pinch-to-zoom (trackpad) or Ctrl+scroll (mouse)
                const rect = el.getBoundingClientRect()
                const cursorX = e.clientX - rect.left
                const cursorY = e.clientY - rect.top
                const zoomFactor = 1 - e.deltaY * 0.01

                setCanvasZoom(prev => {
                    const next = Math.min(10, Math.max(0.2, prev * zoomFactor))
                    const s = next / prev
                    setCanvasPan(p => ({
                        x: cursorX - s * (cursorX - p.x),
                        y: cursorY - s * (cursorY - p.y),
                    }))
                    return next
                })
            } else {
                // Two-finger scroll (trackpad) or regular scroll (mouse) → pan
                setCanvasPan(p => ({
                    x: p.x - e.deltaX,
                    y: p.y - e.deltaY,
                }))
            }
        }

        const onGesture = (e) => {
            if (pointerOverCanvasRef.current) e.preventDefault()
        }

        window.addEventListener('wheel', onWheel, { passive: false, capture: true })
        window.addEventListener('gesturestart', onGesture, { passive: false, capture: true })
        window.addEventListener('gesturechange', onGesture, { passive: false, capture: true })
        window.addEventListener('gestureend', onGesture, { passive: false, capture: true })

        return () => {
            el.removeEventListener('pointerenter', onEnter)
            el.removeEventListener('pointerleave', onLeave)
            window.removeEventListener('wheel', onWheel, { capture: true })
            window.removeEventListener('gesturestart', onGesture, { capture: true })
            window.removeEventListener('gesturechange', onGesture, { capture: true })
            window.removeEventListener('gestureend', onGesture, { capture: true })
        }
    }, [loading, selectedElementIndex])

    // Click and drag on empty canvas space to pan
    const handleCanvasPanStart = useCallback((e) => {
        if (e.button === 0 || e.button === 1) {
            e.preventDefault()
            setIsPanning(true)
            panStartRef.current = { x: e.clientX - canvasPan.x, y: e.clientY - canvasPan.y }
        }
    }, [canvasPan])

    useEffect(() => {
        if (!isPanning) return
        const handleMove = (e) => {
            if (!panStartRef.current) return
            setCanvasPan({
                x: e.clientX - panStartRef.current.x,
                y: e.clientY - panStartRef.current.y,
            })
        }
        const handleUp = () => {
            setIsPanning(false)
            panStartRef.current = null
        }
        window.addEventListener('pointermove', handleMove)
        window.addEventListener('pointerup', handleUp)
        return () => {
            window.removeEventListener('pointermove', handleMove)
            window.removeEventListener('pointerup', handleUp)
        }
    }, [isPanning])

    const zoomToCenter = useCallback((factor) => {
        const el = canvasRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const cx = rect.width / 2
        const cy = rect.height / 2
        setCanvasZoom(prev => {
            const next = Math.min(10, Math.max(0.2, prev * factor))
            const s = next / prev
            setCanvasPan(p => ({
                x: cx - s * (cx - p.x),
                y: cy - s * (cy - p.y),
            }))
            return next
        })
    }, [])

    const resetCanvasView = useCallback(() => {
        const el = canvasRef.current
        if (!el) { setCanvasZoom(DEFAULT_CANVAS_ZOOM); setCanvasPan({ x: 0, y: 0 }); return }
        const rect = el.getBoundingClientRect()
        setCanvasZoom(DEFAULT_CANVAS_ZOOM)
        setCanvasPan({
            x: (rect.width - CANVAS_W * DEFAULT_CANVAS_ZOOM) / 2,
            y: (rect.height - CANVAS_H * DEFAULT_CANVAS_ZOOM) / 2,
        })
        if (selectedElementIndex !== null) {
            delete canvasViewMapRef.current[selectedElementIndex]
        }
    }, [selectedElementIndex])

    const computeFrameView = useCallback((element) => {
        const el = canvasRef.current
        if (!el) return null
        const items = element?.items?.filter(i => i.asset || i.generation || i.url || i.type === 'text' || i.type === 'sketch') || []
        const rect = el.getBoundingClientRect()
        if (!items.length) {
            return { zoom: DEFAULT_CANVAS_ZOOM, pan: { x: (rect.width - CANVAS_W * DEFAULT_CANVAS_ZOOM) / 2, y: (rect.height - CANVAS_H * DEFAULT_CANVAS_ZOOM) / 2 } }
        }

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (const item of items) {
            minX = Math.min(minX, item.x)
            minY = Math.min(minY, item.y)
            maxX = Math.max(maxX, item.x + item.width)
            maxY = Math.max(maxY, item.y + (item.height || item.width))
        }

        const bboxX = (minX / 100) * CANVAS_W
        const bboxY = (minY / 100) * CANVAS_H
        const bboxW = ((maxX - minX) / 100) * CANVAS_W
        const bboxH = ((maxY - minY) / 100) * CANVAS_H

        if (bboxW <= 0 || bboxH <= 0) {
            return { zoom: DEFAULT_CANVAS_ZOOM, pan: { x: (rect.width - CANVAS_W * DEFAULT_CANVAS_ZOOM) / 2, y: (rect.height - CANVAS_H * DEFAULT_CANVAS_ZOOM) / 2 } }
        }

        const padding = 60
        const viewW = rect.width - padding * 2
        const viewH = rect.height - padding * 2
        const zoom = Math.min(10, Math.max(0.2, Math.min(viewW / bboxW, viewH / bboxH)))
        const panX = (rect.width - bboxW * zoom) / 2 - bboxX * zoom
        const panY = (rect.height - bboxH * zoom) / 2 - bboxY * zoom
        return { zoom, pan: { x: panX, y: panY } }
    }, [])

    const frameAllObjects = useCallback(() => {
        const view = computeFrameView(selectedElement)
        if (!view) return
        setCanvasZoom(view.zoom)
        setCanvasPan(view.pan)
    }, [selectedElement, computeFrameView])

    // ==============================
    // KEYBOARD SHORTCUTS
    // ==============================
    useEffect(() => {
        const el = canvasRef.current
        if (!el) return
        const onKeyDown = (e) => {
            if (!pointerOverCanvasRef.current) return
            if (editingTextItem !== null) return
            if (editingAudioNameItem !== null) return
            if (editingSketchItem !== null) {
                if (e.key === 'Escape') {
                    e.preventDefault()
                    commitSketchEdit()
                }
                if (e.key === '[') {
                    e.preventDefault()
                    const item = selectedElementRef.current?.items?.[editingSketchItem]
                    if (item) updateSketchItemProps(editingSketchItem, { brushSize: Math.max(1, (item.brushSize || 3) - 1) })
                }
                if (e.key === ']') {
                    e.preventDefault()
                    const item = selectedElementRef.current?.items?.[editingSketchItem]
                    if (item) updateSketchItemProps(editingSketchItem, { brushSize: Math.min(100, (item.brushSize || 3) + 1) })
                }
                return
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault()
                deleteCanvasItems()
            }
            if (e.key === 'f') {
                e.preventDefault()
                frameAllObjects()
            }
            if (e.key === '+' || e.key === '=') {
                e.preventDefault()
                zoomToCenter(1.2)
            }
            if (e.key === '-') {
                e.preventDefault()
                zoomToCenter(1 / 1.2)
            }
            if (e.key === 's') {
                e.preventDefault()
                setCanvasSnapEnabled(v => !v)
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [deleteCanvasItems, editingTextItem, editingAudioNameItem, editingSketchItem, frameAllObjects, zoomToCenter])

    // ==============================
    // RENDER
    // ==============================
    if (!selectedElement) {
        return (
            <DragPreviewProvider>
            <div className='flex-1 flex flex-row overflow-hidden border-b'>
                <div className='flex-1 flex flex-col overflow-hidden'>
                    <div className='flex items-center justify-end px-3 py-2 border-b border-b-secondary/60 gap-1'>
                        <Button
                            type={generationsOpen ? 'primary' : 'secondary'}
                            size='sm'
                            icon={<FiZap className='w-3 h-3' />}
                            onClick={() => setGenerationsOpen(v => !v)}
                        />
                        <Button
                            type={commentsOpen ? 'primary' : 'secondary'}
                            size='sm'
                            icon={<FiMessageCircle className='w-3 h-3' />}
                            onClick={() => setCommentsOpen(v => !v)}
                        />
                    </div>
                    <div className='flex-1' />
                </div>
                {generationsOpen && (
                    <StoryboardGenerations preview={selectedItemPreview} onAddToCanvas={addCanvasFromUrl} />
                )}
                {commentsOpen && (
                    <StoryboardComments
                        storyboard={storyboard}
                        selectedElementIndex={selectedElementIndex}
                        onSelectElement={setSelectedElementIndex}
                        highlightCommentId={highlightCommentId}
                    />
                )}
            </div>
            </DragPreviewProvider>
        )
    }

    return (
        <DragPreviewProvider>
        <div className='flex-1 flex flex-row overflow-hidden border-b'>
        <div className='flex-1 flex flex-col overflow-hidden'>
            <div className='flex items-center px-3 h-10 border-b border-b-secondary/60 justify-between'>
                <div className='flex items-center gap-10'>
                    <div className='flex items-center gap-2'>
                        <Button type='secondary' size='sm' icon={<FiImage className='w-3 h-3' />} onClick={addCanvasItem}>Asset</Button>
                        <Button type='secondary' size='sm' icon={<FiUpload className='w-3 h-3' />} onClick={addCanvasUpload} disabled={isUploading}>{isUploading ? 'Uploading...' : 'Upload'}</Button>
                        <Button type='secondary' size='sm' icon={<FiType className='w-3 h-3' />} onClick={addCanvasText}>Text</Button>
                        <Button type='secondary' size='sm' icon={<FiEdit2 className='w-3 h-3' />} onClick={addCanvasSketch}>Sketch</Button>
                        <input ref={fileInputRef} type='file' accept='image/*,video/*,audio/*' className='hidden' onChange={handleFileUpload} />
                    </div>
                    
                    {selectedTextItem && <div className='flex items-center gap-2'>
                        <select
                            className='text-[10px] bg-secondary border rounded-sm p-1 text-secondary-foreground outline-none cursor-pointer'
                            value={selectedTextItem.fontFamily || 'sans-serif'}
                            onChange={(e) => updateTextItemProps(selectedTextItem._idx, { fontFamily: e.target.value })}
                        >
                            <option value='sans-serif'>Sans Serif</option>
                            <option value='serif'>Serif</option>
                            <option value='monospace'>Monospace</option>
                            <option value='cursive'>Cursive</option>
                            <option value='fantasy'>Fantasy</option>
                            <option value='system-ui'>System UI</option>
                        </select>
                        <div className='flex items-center gap-0.5'>
                            <button
                                className='p-1 rounded-sm hover:bg-secondary text-secondary-foreground/70 cursor-pointer'
                                onClick={() => updateTextItemProps(selectedTextItem._idx, { fontSize: Math.max(8, (selectedTextItem.fontSize || 24) - 2) })}
                            ><FiMinus className='w-3 h-3' /></button>
                            <input
                                type='number'
                                className='w-7 text-[10px] text-center bg-secondary border rounded-sm p-1 text-secondary-foreground outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                                value={selectedTextItem.fontSize || 24}
                                min={8}
                                max={200}
                                onChange={(e) => updateTextItemProps(selectedTextItem._idx, { fontSize: Math.max(8, parseInt(e.target.value) || 24) })}
                            />
                            <button
                                className='p-1 rounded-sm hover:bg-secondary text-secondary-foreground/70 cursor-pointer'
                                onClick={() => updateTextItemProps(selectedTextItem._idx, { fontSize: Math.min(200, (selectedTextItem.fontSize || 24) + 2) })}
                            ><FiPlus className='w-3 h-3' /></button>
                        </div>
                        <button
                            className={`p-1 rounded-sm cursor-pointer border ${selectedTextItem.fontWeight === 'bold' ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-transparent hover:bg-secondary/80'}`}
                            title='Bold'
                            onClick={() => updateTextItemProps(selectedTextItem._idx, { fontWeight: selectedTextItem.fontWeight === 'bold' ? 'normal' : 'bold' })}
                        ><FiBold className='w-3 h-3' /></button>
                        <div className='flex items-center'>
                            {[
                                { value: 'left', icon: <FiAlignLeft className='w-3 h-3' /> },
                                { value: 'center', icon: <FiAlignCenter className='w-3 h-3' /> },
                                { value: 'right', icon: <FiAlignRight className='w-3 h-3' /> },
                            ].map((a, i) => (
                                <button
                                    key={a.value}
                                    className={`p-1 cursor-pointer border ${(selectedTextItem.textAlign || 'left') === a.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-transparent hover:bg-secondary/80'} ${i === 0 ? 'rounded-l-sm' : i === 2 ? 'rounded-r-sm' : ''}`}
                                    title={`Align ${a.value}`}
                                    onClick={() => updateTextItemProps(selectedTextItem._idx, { textAlign: a.value })}
                                >{a.icon}</button>
                            ))}
                        </div>
                        <ColorPickerSwatch label='Color' value={selectedTextItem.color || '#ffffff'} onChange={(v) => updateTextItemProps(selectedTextItem._idx, { color: v })} />
                        <ColorPickerSwatch label='Bg' value={selectedTextItem.background || 'transparent'} onChange={(v) => updateTextItemProps(selectedTextItem._idx, { background: v })} />
                    </div>}

                    {selectedSketchItem && editingSketchItem !== null && <div className='flex items-center gap-2'>
                        <div className='flex items-center'>
                            <button
                                className={`px-1.5 py-0.5 text-[10px] cursor-pointer border rounded-l-sm ${sketchMode === 'draw' ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-transparent hover:bg-secondary/80'}`}
                                onClick={() => setSketchMode('draw')}
                            >Draw</button>
                            <button
                                className={`px-1.5 py-0.5 text-[10px] cursor-pointer border rounded-r-sm ${sketchMode === 'erase' ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-transparent hover:bg-secondary/80'}`}
                                onClick={() => setSketchMode('erase')}
                            >Erase</button>
                        </div>
                        <ColorPickerSwatch label='Brush' value={selectedSketchItem.brushColor || '#000000'} onChange={(v) => updateSketchItemProps(selectedSketchItem._idx, { brushColor: v })} />
                        <ColorPickerSwatch label='Bg' value={selectedSketchItem.bgColor || '#ffffff'} onChange={(v) => updateSketchItemProps(selectedSketchItem._idx, { bgColor: v })} />
                        <div className='flex items-center gap-0.5'>
                            <label className='text-[10px] text-secondary-foreground/50'>Size</label>
                            <button
                                className='p-0.5 rounded-sm hover:bg-secondary text-secondary-foreground/70 cursor-pointer'
                                onClick={() => updateSketchItemProps(selectedSketchItem._idx, { brushSize: Math.max(1, (selectedSketchItem.brushSize || 3) - 1) })}
                            ><FiMinus className='w-2.5 h-2.5' /></button>
                            <input
                                type='number'
                                className='w-7 text-[10px] text-center bg-secondary border rounded-sm py-0.5 text-secondary-foreground outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                                value={selectedSketchItem.brushSize || 3}
                                min={1}
                                max={100}
                                onChange={(e) => updateSketchItemProps(selectedSketchItem._idx, { brushSize: Math.max(1, parseInt(e.target.value) || 3) })}
                            />
                            <button
                                className='p-0.5 rounded-sm hover:bg-secondary text-secondary-foreground/70 cursor-pointer'
                                onClick={() => updateSketchItemProps(selectedSketchItem._idx, { brushSize: Math.min(100, (selectedSketchItem.brushSize || 3) + 1) })}
                            ><FiPlus className='w-2.5 h-2.5' /></button>
                        </div>
                        <Button type='secondary' size='sm' onClick={() => sketchCanvasRef.current?.clear()}>Clear</Button>
                        <Button type='primary' size='sm' onClick={commitSketchEdit}>Done</Button>
                    </div>}
                </div>

                <div className='flex items-center gap-10'>
                    <Button type='secondary' size='sm' icon={<FiDownload className='w-3 h-3' />} onClick={downloadSelectedItems} disabled={downloadableSelectedItems.length === 0}>Download</Button>
                    <div className='flex items-center overflow-hidden rounded-md'>
                        <Button type='secondary' className='rounded-none' size='sm's disabled={selectedItemIndices.size === 0} icon={<RiSendToBack className='w-3 h-3' />} onClick={sendToBack} title='Send to back' />
                        <Button type='secondary' className='rounded-none' size='sm' disabled={selectedItemIndices.size === 0} icon={<RiSendBackward className='w-3 h-3' />} onClick={moveBackward} title='Move backward' />
                        <Button type='secondary' className='rounded-none' size='sm' disabled={selectedItemIndices.size === 0} icon={<RiBringForward className='w-3 h-3' />} onClick={moveForward} title='Move forward' />
                        <Button type='secondary' className='rounded-none' size='sm' disabled={selectedItemIndices.size === 0} icon={<RiBringToFront className='w-3 h-3' />} onClick={bringToFront} title='Bring to front' />
                    </div>
                    <div className='flex items-center gap-1'>
                        <Button title='GenAI' type={generationsOpen ? 'primary' : 'secondary'} size='sm' icon={<FiZap className='w-3 h-3' />} onClick={() => setGenerationsOpen(v => !v)} />
                        <Button title='Comments' type={commentsOpen ? 'primary' : 'secondary'} size='sm' icon={<FiMessageCircle className='w-3 h-3' />} onClick={() => setCommentsOpen(v => !v)} />
                    </div>
                </div>
            </div>
            <div
                ref={canvasRef}
                className={`flex-1 relative overflow-hidden touch-none ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={{
                    backgroundColor: 'rgba(25,25,25,1)',
                    ...(canvasZoom >= 0.5 ? {
                        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)',
                        backgroundSize: `${20 * canvasZoom}px ${20 * canvasZoom}px`,
                        backgroundPosition: `${canvasPan.x}px ${canvasPan.y}px`,
                    } : {}),
                }}
                onClick={handleCanvasBackgroundClick}
                onPointerDown={handleCanvasPanStart}
                onDragLeave={handleGenerationDragLeave}
            >
                <div className='absolute top-3 left-3 z-50 flex items-center gap-1'>
                    <span className='text-xs text-secondary-foreground/60'>{selectedElement.name}</span>
                </div>
                {/* Floating canvas controls */}
                <div className='absolute top-3 right-3 z-50 flex items-center gap-1 bg-black/50  rounded-md px-1.5 py-1'>
                    <Button type={canvasSnapEnabled ? 'primary' : 'secondary'} size='xs' icon={<TbMagnet className='w-3 h-3' />} onClick={() => setCanvasSnapEnabled(v => !v)} title={canvasSnapEnabled ? 'Canvas snapping on (S)' : 'Canvas snapping off (S)'} />
                    <Button type={textElementsVisible ? 'primary' : 'secondary'} size='xs' icon={<LuLetterText className='w-3 h-3' />} onClick={() => setTextElementsVisible(v => !v)} title={textElementsVisible ? 'Hide text/script elements' : 'Show text/script elements'} />
                    <Button type={visualElementsVisible ? 'primary' : 'secondary'} size='xs' icon={<FiImage className='w-3 h-3' />} onClick={() => setVisualElementsVisible(v => !v)} title={visualElementsVisible ? 'Hide visual elements (image, video, audio, sketch)' : 'Show visual elements'} />
                    <Button type={showTypes ? 'primary' : 'secondary'} size='xs' icon={<FiInfo className='w-3 h-3' />} onClick={() => setShowTypes(v => !v)} title={showTypes ? 'Hide type labels' : 'Show type labels'} />
                    <div className='w-px h-4 bg-secondary-foreground/15 mx-0.5' />
                    <Button type='secondary' size='xs' icon={<FiZoomIn className='w-3 h-3' />} onClick={() => zoomToCenter(1.2)} title='Zoom in (+)' />
                    <button className='text-[10px] font-mono text-secondary-foreground/60 hover:text-secondary-foreground px-1 min-w-8 text-center cursor-pointer' onClick={resetCanvasView}>
                        {Math.round(canvasZoom * 100)}%
                    </button>
                    <Button type='secondary' size='xs' icon={<FiZoomOut className='w-3 h-3' />} onClick={() => zoomToCenter(1 / 1.2)} title='Zoom out (-)' />
                    <div className='w-px h-4 bg-secondary-foreground/15 mx-0.5' />
                    <Button type='secondary' size='xs' icon={<FiMaximize className='w-3 h-3' />} onClick={frameAllObjects} title='Fit all objects (F)' />
                </div>
                <div
                    className='absolute origin-top-left'
                    style={{
                        width: `${CANVAS_W}px`,
                        height: `${CANVAS_H}px`,
                        transform: `translate(${canvasPan.x}px, ${canvasPan.y}px) scale(${canvasZoom})`,
                        willChange: 'transform',
                    }}
                    onDragOver={handleCanvasDragOver}
                    onDrop={handleCanvasDrop}
                >
                {/* Canvas border */}
                <div className='absolute inset-0 border-2 border-secondary-foreground/30 rounded-sm pointer-events-none' />
                {selectedElement.items.map((item, idx) => {
                        const { url: itemUrl, thumbUrl, mediaType: itemMediaType } = getItemMedia(item)
                        const isText = item.type === 'text'
                        const isScript = item.type === 'script'
                        const isSketch = item.type === 'sketch'
                        const isTextLike = isText || isScript
                        if (!isTextLike && !isSketch && !itemUrl && !thumbUrl) return null
                        if (isTextLike && !textElementsVisible) return null
                        const isVisual = isSketch || itemMediaType === 'video' || itemMediaType === 'image' || itemMediaType === 'audio'
                        if (isVisual && !visualElementsVisible) return null
                        // For script items, read live speech from episode context,
                        // falling back to the populated script object from the API
                        const scriptId = isScript ? (typeof item.script === 'object' ? item.script?._id : item.script) : null
                        const liveText = isScript
                            ? (findScriptInEpisode(scriptId)?.speech ?? item.script?.speech ?? '')
                            : (item.text || '')
                        const isSelected = selectedItemIndices.has(idx)
                        const isDropTarget = dropTargetItemIndex === idx
                        const isVideo = itemMediaType === 'video'
                        const isImage = itemMediaType === 'image' && !isSketch
                        const isAudio = itemMediaType === 'audio'
                        const isActiveScript = isScript && activeScriptId && scriptId === activeScriptId
                        const uiZoom = Math.max(canvasZoom, 0.25)
                        return (
                            <div
                                key={item._id || idx}
                                className='absolute cursor-move'
                                style={{
                                    left: `${item.x}%`,
                                    top: `${item.y}%`,
                                    width: `${item.width}%`,
                                    ...((isTextLike || isSketch || isAudio) ? { height: `${item.height}%` } : {}),
                                    zIndex: item.zIndex || 0,
                                    willChange: 'left, top, width',
                                    transform: 'translateZ(0)',
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onPointerDown={(e) => handleCanvasPointerDown(e, idx)}
                            >
                                {isImage && (
                                    <img
                                        src={thumbUrl || itemUrl}
                                        alt={item.name || ''}
                                        className='w-full h-auto block rounded-sm pointer-events-none select-none'
                                        draggable={false}
                                    />
                                )}
                                {isVideo && (
                                    <div className='relative group/video'>
                                        <video
                                            ref={el => { if (el) videoRefs.current[`${selectedElementIndex}-${idx}`] = el }}
                                            src={itemUrl}
                                            className='w-full h-auto block rounded-sm pointer-events-none select-none'
                                            loop
                                            muted
                                            playsInline
                                            draggable={false}
                                        />
                                        {!playingVideos[`${selectedElementIndex}-${idx}`] && (
                                            <div className='absolute inset-0 flex items-center justify-center pointer-events-none z-10'>
                                                <button
                                                    className='rounded-full bg-black/50 hover:bg-black/70 transition-colors flex items-center justify-center pointer-events-auto cursor-pointer'
                                                    style={{ width: `${40 / uiZoom}px`, height: `${40 / uiZoom}px` }}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        const key = `${selectedElementIndex}-${idx}`
                                                        const vid = videoRefs.current[key]
                                                        if (!vid) return
                                                        vid.play()
                                                        setPlayingVideos(prev => ({ ...prev, [key]: true }))
                                                    }}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                >
                                                    <IoMdPlay style={{ width: `${20 / uiZoom}px`, height: `${20 / uiZoom}px` }} className='text-white ml-0.5' />
                                                </button>
                                            </div>
                                        )}
                                        {playingVideos[`${selectedElementIndex}-${idx}`] && (
                                            <div className='absolute inset-0 flex items-center justify-center pointer-events-none z-10'>
                                                <button
                                                    className='rounded-full bg-black/50 hover:bg-black/70 transition-colors flex items-center justify-center pointer-events-auto cursor-pointer opacity-0 group-hover/video:opacity-100'
                                                    style={{ width: `${40 / uiZoom}px`, height: `${40 / uiZoom}px` }}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        const key = `${selectedElementIndex}-${idx}`
                                                        const vid = videoRefs.current[key]
                                                        if (!vid) return
                                                        vid.pause()
                                                        setPlayingVideos(prev => ({ ...prev, [key]: false }))
                                                    }}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                >
                                                    <IoMdPause style={{ width: `${20 / uiZoom}px`, height: `${20 / uiZoom}px` }} className='text-white' />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {isAudio && (
                                    <div className='w-full h-full flex items-center gap-1 px-1.5 py-1 bg-secondary/50 rounded-sm'>
                                        <audio
                                            ref={el => { if (el) audioRefs.current[`${selectedElementIndex}-${idx}`] = el }}
                                            src={itemUrl}
                                            onEnded={() => setPlayingAudio(prev => ({ ...prev, [`${selectedElementIndex}-${idx}`]: false }))}
                                        />
                                        <button
                                            type='button'
                                            className='flex-shrink-0 rounded p-0.5 hover:bg-secondary-foreground/20 transition-colors cursor-pointer flex items-center justify-center'
                                            style={{ width: `${18 / uiZoom}px`, height: `${18 / uiZoom}px` }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                const key = `${selectedElementIndex}-${idx}`
                                                const el = audioRefs.current[key]
                                                if (!el) return
                                                if (playingAudio[key]) {
                                                    el.pause()
                                                    setPlayingAudio(prev => ({ ...prev, [key]: false }))
                                                } else {
                                                    el.play()
                                                    setPlayingAudio(prev => ({ ...prev, [key]: true }))
                                                }
                                            }}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            title={playingAudio[`${selectedElementIndex}-${idx}`] ? 'Pause' : 'Play'}
                                        >
                                            {playingAudio[`${selectedElementIndex}-${idx}`] ? (
                                                <IoMdPause style={{ width: `${20 / uiZoom}px`, height: `${20 / uiZoom}px` }} className='text-secondary-foreground' />
                                            ) : (
                                                <IoMdPlay style={{ width: `${20 / uiZoom}px`, height: `${20 / uiZoom}px` }} className='text-secondary-foreground ml-0.5' />
                                            )}
                                        </button>
                                        {editingAudioNameItem === idx ? (
                                            <input
                                                ref={editingAudioNameItem === idx ? editingAudioNameRef : null}
                                                type='text'
                                                className='flex-1 min-w-0 bg-transparent border-none outline-none text-secondary-foreground px-0'
                                                style={{ fontSize: `${10 / uiZoom}px` }}
                                                defaultValue={item.name || 'Audio'}
                                                onBlur={(e) => commitAudioNameEdit(idx, e.target.value)}
                                                onKeyDown={(e) => {
                                                    e.stopPropagation()
                                                    if (e.key === 'Enter') {
                                                        e.target.blur()
                                                    }
                                                    if (e.key === 'Escape') {
                                                        commitAudioNameEdit(idx, item.name || 'Audio')
                                                        e.target.blur()
                                                    }
                                                }}
                                                onPointerDown={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span
                                                className='text-[10px] text-secondary-foreground truncate min-w-0 flex-1'
                                                style={{ fontSize: `${10 / uiZoom}px` }}
                                                onDoubleClick={(e) => {
                                                    e.stopPropagation()
                                                    startEditingAudioName(idx)
                                                }}
                                            >
                                                {item.name || 'Audio'}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {/* Type indicator above the element */}
                                {showTypes && (() => {
                                    let TypeIcon = null
                                    let typeLabel = ''
                                    if (isSketch) { TypeIcon = FiEdit2; typeLabel = 'Sketch' }
                                    else if (isScript) { TypeIcon = LuLetterText; typeLabel = 'Script' }
                                    else if (isText) { TypeIcon = FiType; typeLabel = 'Text' }
                                    else if (isImage) { TypeIcon = FiImage; typeLabel = 'Image' }
                                    else if (isVideo) { TypeIcon = FiFilm; typeLabel = 'Video' }
                                    else if (isAudio) { TypeIcon = FiVolume2; typeLabel = 'Audio' }
                                    if (!TypeIcon) return null
                                    return (
                                        <div
                                            className='absolute pointer-events-none z-10 flex items-center gap-0.5'
                                            style={{
                                                top: `${-18 / uiZoom}px`,
                                                left: `${2 / uiZoom}px`,
                                            }}
                                        >
                                            <TypeIcon style={{ width: `${12 / uiZoom}px`, height: `${12 / uiZoom}px` }} className='text-secondary-foreground/50' />
                                            <span style={{ fontSize: `${10 / uiZoom}px` }} className='text-secondary-foreground/50'>{typeLabel}</span>
                                        </div>
                                    )
                                })()}
                                {isSketch && editingSketchItem === idx ? (
                                    <div className='w-full h-full rounded-sm overflow-hidden'>
                                        <DrawingCanvas
                                            ref={sketchCanvasRef}
                                            width={item.canvasWidth || SKETCH_CANVAS_W}
                                            height={item.canvasHeight || SKETCH_CANVAS_H}
                                            initialData={item.url || undefined}
                                            bgColor={item.bgColor || '#ffffff'}
                                            brushColor={item.brushColor || '#000000'}
                                            brushSize={item.brushSize || 3}
                                            mode={sketchMode}
                                        />
                                    </div>
                                ) : isSketch ? (
                                    <div
                                        className='w-full h-full rounded-sm overflow-hidden select-none'
                                        style={{ backgroundColor: item.bgColor || '#ffffff' }}
                                        onDoubleClick={(e) => {
                                            e.stopPropagation()
                                            startEditingSketch(idx)
                                        }}
                                    >
                                        {(sketchPreview?.index === idx ? sketchPreview.dataUrl : item.url) ? (
                                            <img
                                                src={sketchPreview?.index === idx ? sketchPreview.dataUrl : item.url}
                                                alt='Sketch'
                                                className='w-full h-full block pointer-events-none select-none object-contain'
                                                draggable={false}
                                            />
                                        ) : (
                                            <div className='w-full h-full flex items-center justify-center'>
                                                <FiEdit2 className='text-secondary-foreground/20' style={{ width: `${40 / Math.max(canvasZoom, 0.25)}px`, height: `${40 / Math.max(canvasZoom, 0.25)}px` }} />
                                            </div>
                                        )}
                                        {uploadingSketchItem === idx && (
                                            <div className='absolute inset-0 flex items-center justify-center bg-black/30 rounded-sm z-10'>
                                                <svg className='animate-spin' style={{ width: `${24 / Math.max(canvasZoom, 0.25)}px`, height: `${24 / Math.max(canvasZoom, 0.25)}px` }} viewBox='0 0 24 24' fill='none'>
                                                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='white' strokeWidth='3' />
                                                    <path className='opacity-75' fill='white' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                                {isTextLike && editingTextItem === idx ? (
                                    <textarea
                                        ref={editingTextRef}
                                        className={`w-full h-full border-none outline-none resize-none block ${isScript ? 'rounded-lg' : 'rounded-sm'}`}
                                        style={{
                                            fontFamily: item.fontFamily || 'sans-serif',
                                            fontSize: `${item.fontSize || 16}px`,
                                            fontWeight: item.fontWeight || 'normal',
                                            fontStyle: isScript ? 'italic' : 'normal',
                                            color: item.color || '#ffffff',
                                            background: item.background || 'transparent',
                                            textAlign: item.textAlign || 'left',
                                            lineHeight: 1.2,
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                            padding: '8px',
                                            caretColor: item.color || '#ffffff',
                                            overflow: 'auto',
                                        }}
                                        defaultValue={isScript ? liveText : (item.text || '')}
                                        onBlur={(e) => commitTextEdit(idx, e.target.value)}
                                        onKeyDown={(e) => {
                                            e.stopPropagation()
                                            if (e.key === 'Escape') {
                                                e.target.blur()
                                            }
                                        }}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                ) : isTextLike ? (
                                    <div
                                        className={`w-full h-full select-none ${isScript ? 'rounded-lg' : 'rounded-sm'}`}
                                        style={{
                                            fontFamily: item.fontFamily || 'sans-serif',
                                            fontSize: `${item.fontSize || 16}px`,
                                            fontWeight: item.fontWeight || 'normal',
                                            fontStyle: isScript ? 'italic' : 'normal',
                                            color: item.color || '#ffffff',
                                            textAlign: item.textAlign || 'left',
                                            background: item.background || 'transparent',
                                            lineHeight: 1.2,
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                            overflow: 'hidden',
                                            padding: '8px',
                                        }}
                                        onDoubleClick={(e) => {
                                            e.stopPropagation()
                                            startEditingText(idx)
                                        }}
                                    >
                                        {isScript ? (liveText || 'Script') : (item.text || 'Text')}
                                    </div>
                                ) : null}
                                {/* Selection border overlay */}
                                {isSelected && (
                                    <div
                                        className={`absolute inset-0 border-primary pointer-events-none z-10 ${isScript ? 'rounded-lg' : 'rounded-sm'}`}
                                        style={{ borderWidth: `${2 / uiZoom}px`, borderStyle: 'solid', transform: 'translateZ(0)' }}
                                    />
                                )}
                                {/* Active script highlight (playhead position) */}
                                {isActiveScript && (
                                    <div
                                        className='absolute pointer-events-none z-10 rounded-lg border-amber-400/80 bg-amber-400/10'
                                        style={{ inset: `${-3 / uiZoom}px`, borderWidth: `${2 / uiZoom}px`, borderStyle: 'solid', transform: 'translateZ(0)' }}
                                    />
                                )}
                                {/* Drop target highlight (generation drag-over) */}
                                {isDropTarget && (
                                    <div
                                        className={`absolute inset-0 pointer-events-none z-10 bg-primary/20 border-2 border-primary ${isScript ? 'rounded-lg' : 'rounded-sm'}`}
                                        style={{ borderStyle: 'dashed', transform: 'translateZ(0)' }}
                                    />
                                )}
                                {/* Resize handle (not shown for audio - audio is draggable only) */}
                                {isSelected && selectedItemIndices.size === 1 && !isAudio && (
                                    <div
                                        className='absolute bottom-0 right-0 bg-primary rounded-tl-sm cursor-se-resize z-20'
                                        style={{ width: `${12 / uiZoom}px`, height: `${12 / uiZoom}px` }}
                                        onPointerDown={(e) => handleCanvasResizePointerDown(e, idx)}
                                    />
                                )}
                            </div>
                        )
                })}
                </div>
            </div>
        </div>
        {generationsOpen && (
            <StoryboardGenerations preview={selectedItemPreview} onAddToCanvas={addCanvasFromUrl} />
        )}
        {commentsOpen && (
            <StoryboardComments
                storyboard={storyboard}
                selectedElementIndex={selectedElementIndex}
                onSelectElement={setSelectedElementIndex}
                highlightCommentId={highlightCommentId}
            />
        )}
        </div>
        </DragPreviewProvider>
    )
}
